import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/api';

// 🎬📝 Admin strip for the credits card (09-23, MOVIE_NIGHT_UX_HANDOFF §3.7,
// ruling 16): what people answered at the wrap of a house event. Mirrors
// AdminBugReports. Backend = adminEventFeedback in handler.js (admin-auth,
// src/ChatGame/eventFeedback.js): { days, feedback[], counts: { total,
// by_template }, tallies: { [template]: { [key]: { [label]: n } } } }.
// Free text is user-typed: it renders as TEXT only, never HTML.

export default function AdminEventFeedback({ adminFetch, adminKey, onCounts }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!adminKey) return;
    try {
      const res = await adminFetch(`${API_BASE_URL}/api/takeGameStep?adminEventFeedback=true&days=30`);
      const json = await res.json();
      if (json.success) { setData(json); setError(null); if (onCounts) onCounts(json.counts || {}); }
      else setError(json.error || 'unavailable');
    } catch (err) {
      setError(err.message);
    }
  }, [adminKey, adminFetch, onCounts]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div style={{ fontSize: 12.5, color: '#d98a8a' }}>🎬 {error}</div>;
  if (!data) return <div style={{ fontSize: 12.5, color: '#9aa7b8' }}>Loading…</div>;

  const rows = data.feedback || [];
  const tallies = data.tallies || {};
  const byTemplate = (data.counts && data.counts.by_template) || {};
  const btn = { padding: '2px 8px', fontSize: 11.5, borderRadius: 5, border: '1px solid #39455a', background: '#151d29', color: '#cfd8e6', cursor: 'pointer' };

  return (
    <div style={{ fontSize: 12.5, color: '#cfd8e6' }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ color: '#e0c36a' }}>{(data.counts && data.counts.total) || 0} answers</strong>
        {Object.keys(byTemplate).map((tpl) => <span key={tpl}>{tpl} {byTemplate[tpl]}</span>)}
        <button type="button" style={btn} onClick={load}>↻ Refresh</button>
      </div>
      {/* the tallies first: one line per question, the counts per option */}
      {Object.keys(tallies).map((tpl) => (
        <div key={tpl} style={{ borderTop: '1px solid #2a3547', padding: '6px 0' }}>
          <div style={{ fontWeight: 700, color: '#e8ecf3' }}>{tpl}</div>
          {Object.keys(tallies[tpl] || {}).map((key) => (
            <div key={key} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', color: '#9aa7b8' }}>
              <span style={{ minWidth: 70, color: '#cfd8e6' }}>{key}</span>
              {Object.entries(tallies[tpl][key] || {}).map(([label, n]) => <span key={label}>{label} <b style={{ color: '#e8ecf3' }}>{n}</b></span>)}
            </div>
          ))}
        </div>
      ))}
      {rows.length === 0 && <div style={{ color: '#9aa7b8' }}>No answers yet.</div>}
      {rows.map((r) => {
        const ctx = r.context || {};
        const open = openId === r.id;
        const answers = r.answers && typeof r.answers === 'object' ? r.answers : {};
        return (
          <div key={r.id} style={{ borderTop: '1px solid #2a3547', padding: '8px 0' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <span style={{ color: '#e0c36a', fontWeight: 700 }}>#{r.id}</span>
              <span style={{ color: '#9aa7b8' }}>{new Date(r.created_at).toLocaleString()}</span>
              <span>{r.reporter_name || 'visitor'}{r.verified ? '' : ' (unverified)'}</span>
              <span style={{ color: '#9ec5ff' }}>{r.event_template || '?'}{r.event_id ? ` · ${String(r.event_id).slice(0, 8)}` : ''}</span>
              {r.space_id && (
                <a href={`/game-step?gameId=${encodeURIComponent(r.space_id)}`} target="_blank" rel="noreferrer" style={{ color: '#9ec5ff' }}>
                  {r.space_name || r.space_id.slice(0, 8)}
                </a>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '4px 0' }}>
              {Object.entries(answers).map(([k, v]) => <span key={k}><span style={{ color: '#9aa7b8' }}>{k}</span> {String(v)}</span>)}
            </div>
            {r.text && <div style={{ margin: '2px 0 6px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: '#e8ecf3' }}>{r.text}</div>}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button type="button" style={btn} onClick={() => setOpenId(open ? null : r.id)}>{open ? 'hide context' : 'context'}</button>
            </div>
            {open && (
              <pre style={{ margin: '6px 0 0', padding: 8, background: '#0f1520', borderRadius: 6, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {JSON.stringify(ctx, null, 2)}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}
